'use client';

import { LoadingIcon } from '@insta-club/components/icons';
import React, { useEffect, useMemo, useRef } from 'react';
import { FetcherOptions } from 'src/libs/api/config/fetcher';
import useSWRInfinite from 'swr/infinite';

type Meta = { current_page: number; last_page: number };
type PageShape<T> = { data: T[]; meta: Meta };

type InfiniteScrollProps<T> = {
    title?: string | React.ReactNode;
    fetcher: (url: string, options: FetcherOptions) => Promise<PageShape<T>>;
    endpoint: string | null;
    pageSize?: number;
    renderItem: (item: T, index: number) => React.ReactNode;
    renderLoading?: () => React.ReactNode;
    renderError?: (error: Error) => React.ReactNode;
    renderEmpty?: () => React.ReactNode;
    scrollParentRef?: React.RefObject<HTMLElement | null>;
};

export default function InfiniteScroll<T>({
    title,
    fetcher,
    endpoint,
    pageSize = 10,
    renderItem,
    renderLoading = () => <div>در حال بارگذاری...</div>,
    renderError = (error) => <div>خطا: {error.message}</div>,
    renderEmpty = () => <div>لیست داده ها خالی میباشد</div>,
    scrollParentRef
}: InfiniteScrollProps<T>) {
    const getKey = (pageIndex: number, previousPageData: PageShape<T> | null) => {
        if (!endpoint) return null;
        if (previousPageData) {
            const { current_page, last_page } = previousPageData.meta || {};
            if (typeof current_page === 'number' && typeof last_page === 'number' && current_page >= last_page) {
                return null;
            }
        }
        const page = pageIndex + 1;
        return [endpoint, page, pageSize] as const;
    };

    // - size TODO: remove size from useSWRInfinite
    const { data, error, isLoading, setSize, isValidating } = useSWRInfinite<PageShape<T>>(
        getKey,
        ([url, page, per]) => fetcher(url, { params: { page, per_page: per } }),
        { revalidateOnFocus: false, dedupingInterval: 800, revalidateFirstPage: false }
    );

    const items: T[] = useMemo(() => (data ? data.flatMap((p) => p.data ?? []) : []), [data]);

    const reachedEnd = useMemo(() => {
        if (!data || data.length === 0) return false;
        const last = data[data.length - 1];
        return last.meta?.current_page >= last.meta?.last_page;
    }, [data]);

    const loadingNextRef = useRef(false);
    useEffect(() => {
        if (!isValidating) loadingNextRef.current = false;
    }, [isValidating]);

    const sentinelRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        const rootEl = scrollParentRef?.current ?? null;

        const io = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting && !loadingNextRef.current && !isLoading && !isValidating && !reachedEnd) {
                    loadingNextRef.current = true;
                    setSize((s) => s + 1);
                }
            },
            {
                root: rootEl ?? null,
                rootMargin: '400px 0px',
                threshold: 0.01
            }
        );

        const node = sentinelRef.current;
        if (node) io.observe(node);
        return () => {
            if (node) io.unobserve(node);
            io.disconnect();
        };
    }, [isLoading, isValidating, reachedEnd, setSize, scrollParentRef]);

    if (error) return renderError(error);
    if (isLoading && !data) return renderLoading();

    return (
        <div className='space-y-3'>
            {!items.length && renderEmpty()}
            {reachedEnd &&
                !!items.length &&
                title &&
                (typeof title === 'string' ? (
                    <h2 className='mb-4 text-base font-semibold text-gray-800'>{title}</h2>
                ) : (
                    title
                ))}
            {items.map((item, i) => renderItem(item, i))}
            {isValidating && (
                <div className='flex items-center justify-center space-x-2 p-2'>
                    <span> در حال بارگذاری بیشتر…</span>
                    <LoadingIcon className='text-primary-600 h-5 w-5 animate-spin' />
                </div>
            )}
            {reachedEnd && items.length > 0 && <div className='p-2 text-gray-700'></div>}
            <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
    );
}

